import type { Account } from "@baton/api";
import type { StorageKey } from "@baton/core";
import { useI18n } from "@baton/i18n";
import { useMemo, useState, type ReactNode } from "react";
import { anyOpen, CollapseAllGlyph, withEvery, type Collapsed } from "../../accounts/collapseAll.tsx";
import { countryName } from "../../accounts/countryName.ts";
import { byCountry, groupClients } from "../../accounts/grouping.ts";
import { regionOnlyLabel } from "../../accounts/location.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { GlideList } from "./GlideList.tsx";
import { HeadingGroup } from "./HeadingGroup.tsx";
import { SIDEBAR_PILL, SidebarSection, SidebarSectionAction, sidebarSectionRowsId } from "./Sidebar.tsx";

/**
 * The REGIONS section of a mode's sidebar that lists clients (`contracts/screens.md` § 020 Accounts and
 * pipeline, the Clients sidebar, and § 028 Client portal, the Tickets mode as amended 2026-10-21): each
 * country a collapsible heading with its flag, its name in the reader's locale and the count of its
 * clients, the clients under it by name, and the clients located by a region alone under a heading of
 * that area after every country, in the order `accounts/grouping.ts` gives every surface that groups
 * clients. The label ends in Collapse all or Expand all. Which headings are collapsed is kept per
 * workspace under the caller's key, since the Clients mode and the Tickets mode list different clients.
 * The caller draws each client's row, and may count each heading and make it a link to its place, as
 * the Tickets mode does with each place's open tickets and board (028 FR-028-071, FR-028-076); the
 * section is absent while there is no client to list.
 */
export interface RegionsSectionProps {
  readonly id: string;
  readonly ws: string;
  readonly clients: readonly Account[];
  readonly collapsedKey: StorageKey<Collapsed>;
  readonly row: (client: Account) => ReactNode;
  /** A heading's count, the count of its clients when absent; the Tickets mode counts their open tickets (028 FR-028-076). */
  readonly headingCount?: (clients: readonly Account[]) => { readonly count: number; readonly label: string };
  /** A heading as a link to its place's page, which the Tickets mode's headings are (028 FR-028-071). */
  readonly headingLink?: (place: { readonly country: string | null; readonly areaCode: string | null; readonly name: string }) => { readonly to: string; readonly current: boolean; readonly toggleLabel: string; readonly onNavigate?: () => void };
}

export function RegionsSection({ id, ws, clients, collapsedKey, row, headingCount, headingLink }: RegionsSectionProps) {
  const { t, locale } = useI18n();
  const runtime = useRuntime();
  const countries = useMemo(() => groupClients(clients, byCountry((code) => countryName(locale, code), { kind: "per_area", label: (area) => regionOnlyLabel(t, area) }), locale), [clients, locale, t]);
  const [collapsed, setCollapsed] = useState<Collapsed>(() => runtime.storage.get(collapsedKey, ws) ?? {});
  const remember = (updated: Collapsed) => {
    runtime.storage.set(collapsedKey, updated, ws);
    setCollapsed(updated);
  };
  if (countries.length === 0) return null;
  const regionIds = countries.map((country) => country.id);
  const regionsOpen = anyOpen(regionIds, collapsed);
  return (
    <SidebarSection
      id={id}
      title={t("nav.clients.section.regions")}
      fill
      action={
        <SidebarSectionAction
          icon={<CollapseAllGlyph open={regionsOpen} />}
          label={regionsOpen ? t("nav.clients.regions.collapseAll") : t("nav.clients.regions.expandAll")}
          controls={sidebarSectionRowsId(id)}
          onClick={() => {
            remember(withEvery(collapsed, regionIds, regionsOpen));
          }}
        />
      }
    >
      {countries.map((country) => {
        const counted = headingCount?.(country.clients) ?? { count: country.clients.length, label: t("nav.clients.clientCount", { count: country.clients.length }) };
        const link = headingLink?.({ country: country.country, areaCode: country.area?.code ?? null, name: country.label });
        return (
        <HeadingGroup
          key={country.id}
          name={country.label}
          country={country.country}
          area={country.area}
          count={counted.count}
          countLabel={counted.label}
          open={!(collapsed[country.id] ?? false)}
          onToggle={() => {
            remember({ ...collapsed, [country.id]: !(collapsed[country.id] ?? false) });
          }}
          {...(link === undefined ? {} : { link })}
        >
          <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL}>
            {country.clients.map(row)}
          </GlideList>
        </HeadingGroup>
        );
      })}
    </SidebarSection>
  );
}
